import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, Signal, inject } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { tapResponse } from '@ngrx/operators';
import { AssetStore } from '@r1-compass-library/store/asset.store';
import { Observable, concat, exhaustMap } from 'rxjs';
import { filterNil } from 'ngxtension/filter-nil';
import { toSignal } from '@angular/core/rxjs-interop';
import { AssetStateMessage, TemplateCriteria, TemplateCriteriaConfig } from '@r1-compass-library/models';
import { TemplateCriteriaService } from '@r1-compass-library/services';
import { tap } from 'rxjs/operators';
import { exhaustOp, rxMutation } from '@angular-architects/ngrx-toolkit';

export interface TemplateCriteriaState {
    template: TemplateCriteria | undefined;
    isLoading: boolean;
    error: string | undefined;
    message: AssetStateMessage | undefined;
}

export const initialState: TemplateCriteriaState = {
    template: undefined,
    isLoading: false,
    error: undefined,
    message: undefined,
};

@Injectable()
export class TemplateCriteriaStore extends AssetStore<TemplateCriteria, TemplateCriteriaState> {
    readonly #templateService = inject(TemplateCriteriaService);
    protected readonly route = inject(ActivatedRoute);

    template$ = this.select(state => state.template);
    template: Signal<TemplateCriteria | undefined> = toSignal(this.template$);
    message$ = this.select(state => state.message);

    assetTypeName = 'Template Criteria';

    constructor() {
        super(initialState);
    }

    reset = this.updater(state => ({
        ...initialState,
    }));

    setIsLoading = this.updater(state => ({
        ...state,
        isLoading: true,
    }));

    setError = this.updater((state, error: HttpErrorResponse) => ({
        ...state,
        isLoading: false,
        error: error.message,
    }));

    setMessage = this.updater((state, message: AssetStateMessage) => ({
        ...state,
        isLoading: false,
        message: message,
    }));

    resetMessage = this.updater(state => ({
        ...state,
        isLoading: false,
        message: undefined,
    }));

    setTemplate = this.updater((state, template: TemplateCriteria) => ({
        ...state,
        template: template,
        isLoading: false,
    }));

    getTemplate = this.effect((id$: Observable<number>) => {
        return id$.pipe(
            tap(() => this.setIsLoading()),
            exhaustMap(id => {
                const userDivisionCodes: string[] = this.usersAvailableDivisions().map(
                    availableDivision => availableDivision.divisionCode
                );
                return this.#templateService.search({ id: id, divisions: userDivisionCodes }).pipe(
                    tapResponse({
                        next: templates => this.handleSearchResponse(templates, this.setTemplate, this.setMessage),
                        error: (err: HttpErrorResponse) => console.error(err),
                    })
                );
            })
        );
    });

    saveTemplate = rxMutation({
        operation: (saveData: { templateId: number; template: Partial<TemplateCriteria> }) => {
            return this.#templateService.update(saveData.templateId, saveData.template);
        },
        operator: exhaustOp,
        onSuccess: updatedTemplate => {
            this.setTemplate(updatedTemplate);
        },
        onError: err => console.error(err),
    });

    saveTemplateAndConfig = rxMutation({
        operation: (saveData: { template: Partial<TemplateCriteria>; config: Partial<TemplateCriteriaConfig> }) => {
            const call: Observable<TemplateCriteria> = saveData.template.id
                ? this.updateTemplateAndData(saveData.template.id, saveData.template, saveData.config)
                : this.createTemplateAndData(saveData.template, saveData.config);
            return call;
        },
        operator: exhaustOp,
        onSuccess: updatedTemplateWithConfig => {
            this.setTemplate(updatedTemplateWithConfig);
        },
        onError: err => console.error(err),
    });

    saveTemplateConfig = this.effect(
        (templateData$: Observable<{ id: number; config: Partial<TemplateCriteriaConfig> }>) => {
            return templateData$.pipe(
                filterNil(),
                exhaustMap(templateData => {
                    return this.#templateService.updateData(templateData.id, templateData.config).pipe(
                        tapResponse({
                            next: template => this.setTemplate(template),
                            error: (err: HttpErrorResponse) => console.error(err),
                        })
                    );
                })
            );
        }
    );

    private updateTemplateAndData(
        templateId: number,
        template: Partial<TemplateCriteria>,
        config: Partial<TemplateCriteriaConfig>
    ): Observable<TemplateCriteria> {
        return concat(
            ...[
                this.#templateService.update(templateId, template),
                this.#templateService.updateData(templateId, config),
            ]
        );
    }

    private createTemplateAndData(
        template: Partial<TemplateCriteria>,
        config: Partial<TemplateCriteriaConfig>
    ): Observable<TemplateCriteria> {
        template.config = config;
        return this.#templateService.create(template);
    }
}
